/**
 * Collection Subscription Footer Utilities
 * Shared functions for collection-subscription-footer.js
 * Pattern follows thuysvers-survey-form-utils.js
 */
(function () {
  'use strict';

  const ACTIVE_STEP_KEY = 'tv_subscription_active_step_handle';
  const ONEOFF_ENABLED_KEY = 'tv_subscription_oneoff_enabled';

  const STEP_PROGRESS_LABELS = {
    soup: 'Soepen',
    dessert: 'Desserts',
    oneoff: 'Eenmalig',
  };

  /**
   * Get cart item kind (meal/soup/dessert) - matches tv-subscription-cart-item-kind.liquid.
   * @param {any} item - Cart line item from /cart.js
   * @returns {'meal'|'soup'|'dessert'}
   */
  function getItemKind(item) {
    if (!item) return 'meal';
    const productType =
      item.product?.type != null && item.product.type !== ''
        ? item.product.type
        : item.product_type != null && item.product_type !== ''
          ? item.product_type
          : '';
    const shopifyTypeDown = (productType || '').toLowerCase();
    const meta = item.product?.metafields?.custom?.product_type;
    const metaValue = meta && typeof meta === 'object' && meta.value != null ? meta.value : meta;
    let productTypeMetaJson = '';
    if (metaValue != null) {
      productTypeMetaJson =
        typeof metaValue === 'string'
          ? metaValue.toLowerCase()
          : (JSON.stringify(metaValue) || '').toLowerCase();
    }
    const isSoupType = shopifyTypeDown === 'soep' || shopifyTypeDown === 'soup';
    if (isSoupType && (productTypeMetaJson.includes('"extras"') || !productTypeMetaJson)) return 'soup';
    if (shopifyTypeDown === 'nagerechten' || shopifyTypeDown === 'toetjes') return 'dessert';
    return 'meal';
  }

  /**
   * Get _profile_key from cart item properties.
   * @param {any} item - Cart line item from /cart.js
   * @returns {string}
   */
  function getItemProfileKey(item) {
    const p = item?.properties;
    if (!p) return '';
    if (typeof p === 'object' && !Array.isArray(p) && p._profile_key != null) return String(p._profile_key).trim();
    if (Array.isArray(p)) {
      const e = p.find((x) => x && (x.name === '_profile_key' || x.key === '_profile_key'));
      return e ? String(e.value || '').trim() : '';
    }
    return '';
  }

  /**
   * @param {any | null} payload
   * @returns {{ profileCount: number; soupCount: number; dessertCount: number }}
   */
  function getStepperCounts(payload) {
    const profilesLength = Array.isArray(payload?.profiles) ? payload.profiles.length : 0;
    const profileCountFromPayload = Number(payload?.profileCount);
    const profileCount =
      profileCountFromPayload >= 1 && profileCountFromPayload <= 6
        ? profileCountFromPayload
        : Math.min(6, Math.max(1, profilesLength || 1));

    let soupCount = 0;
    let dessertCount = 0;
    const addons = Array.isArray(payload?.addOns) ? payload.addOns : Array.isArray(payload?.add_ons) ? payload.add_ons : [];
    addons.forEach((/** @type {{ addonType?: string; addon_type?: string; quantity?: number }} */ addon) => {
      const t = String(addon?.addonType || addon?.addon_type || '').toLowerCase();
      const q = Number(addon?.quantity) || 0;
      if (t === 'soup') soupCount += q;
      else if (t === 'dessert') dessertCount += q;
    });

    return { profileCount, soupCount, dessertCount };
  }

  /**
   * Get current and required counts for a step (person: only meals; soup: only soups; dessert: only desserts).
   * @param {string} handle - Step handle (p1, p2, soup, dessert)
   * @param {{ items?: any[] } | null} cart
   * @param {any | null} payload - Survey result payload
   * @returns {{ current: number; required: number }}
   */
  function getStepCounts(handle, cart, payload) {
    if (!payload || !cart) return { current: 0, required: 0 };

    const profiles = Array.isArray(payload.profiles) ? payload.profiles : [];
    const addons = Array.isArray(payload.addOns) ? payload.addOns : Array.isArray(payload.add_ons) ? payload.add_ons : [];
    let required = 0;

    if (/^p\d+$/i.test(handle)) {
      const match = handle.match(/^p(\d+)$/i);
      const idx = match?.[1] ? Math.max(0, parseInt(match[1], 10) - 1) : 0;
      required = Number(profiles[idx]?.mealsPerWeek || 0);
    } else if (handle === 'soup') {
      addons.forEach((/** @type {{ addonType?: string; quantity?: number }} */ a) => {
        if (String(a?.addonType || '').toLowerCase() === 'soup') required += Number(a?.quantity || 0);
      });
    } else if (handle === 'dessert') {
      addons.forEach((/** @type {{ addonType?: string; quantity?: number }} */ a) => {
        if (String(a?.addonType || '').toLowerCase() === 'dessert') required += Number(a?.quantity || 0);
      });
    } else {
      return { current: 0, required: 0 };
    }

    const items = cart.items || [];
    let current = 0;
    if (/^p\d+$/i.test(handle)) {
      items.forEach((/** @type {any} */ it) => {
        if (getItemProfileKey(it) !== handle || getItemKind(it) !== 'meal') return;
        current += Number(it.quantity || 0);
      });
    } else if (handle === 'soup') {
      items.forEach((/** @type {any} */ it) => {
        if (getItemProfileKey(it) === 'oneoff') return;
        if (getItemKind(it) !== 'soup') return;
        current += Number(it.quantity || 0);
      });
    } else if (handle === 'dessert') {
      items.forEach((/** @type {any} */ it) => {
        if (getItemProfileKey(it) === 'oneoff') return;
        if (getItemKind(it) !== 'dessert') return;
        current += Number(it.quantity || 0);
      });
    }
    return { current, required };
  }

  /**
   * Check if a given step's quota is full (current >= required).
   * @param {string} handle - Step handle (p1, p2, soup, dessert, etc.)
   * @param {{ items?: any[] } | null} cart
   * @param {any | null} payload - Survey result payload
   * @returns {boolean}
   */
  function isStepQuotaFull(handle, cart, payload) {
    if (!payload || !cart) return false;
    const { required } = getStepCounts(handle, cart, payload);
    if (required <= 0) return false;
    const { current } = getStepCounts(handle, cart, payload);
    return current >= required;
  }

  /**
   * Get survey result payload from sessionStorage.
   * @returns {any | null}
   */
  function getSurveyResultPayload() {
    try {
      const raw = sessionStorage.getItem('subscription_survey_result');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * Get recommendations payload (fallback when subscription_survey_result not set).
   * @returns {any | null}
   */
  function getRecommendationsPayload() {
    try {
      const raw = sessionStorage.getItem('recommendations');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /**
   * Reads the exact filter value from the subscription step toggle radio.
   * @param {'soup'|'dessert'} step
   * @returns {string | null}
   */
  function getExactFilterValueForStep(step) {
    try {
      const el = document.querySelector(
        `[data-ecomm-person-toggle-options] input[type="radio"][name="ecomm-step"][value="${CSS.escape(step)}"]`
      );
      if (!(el instanceof HTMLInputElement)) return null;
      const raw = el.dataset?.value ?? el.getAttribute('data-value') ?? '';
      const v = String(raw || '').trim();
      return v || null;
    } catch {
      return null;
    }
  }

  /** @type {{ drawer: HTMLElement; alle: HTMLInputElement|null; aanbevolen: HTMLInputElement|null }|null} */
  let filtersDrawerCache = null;

  /**
   * Cached refs for filters drawer, Alle, and Aanbevolen. Invalidates when drawer is removed from DOM.
   * @returns {{ drawer: HTMLElement; alle: HTMLInputElement|null; aanbevolen: HTMLInputElement|null }|null}
   */
  function getFiltersDrawerRefs() {
    if (filtersDrawerCache) {
      const { drawer } = filtersDrawerCache;
      if (drawer && document.contains(drawer)) return filtersDrawerCache;
      filtersDrawerCache = null;
    }
    const drawer = document.querySelector('#filters-drawer');
    if (!(drawer instanceof HTMLElement)) return null;
    const alle = drawer.querySelector('input[type="checkbox"][data-label="Alle"]');
    const aanbevolen = drawer.querySelector('input[type="checkbox"][data-label="Aanbevolen"]');
    filtersDrawerCache = {
      drawer,
      alle: alle instanceof HTMLInputElement ? alle : null,
      aanbevolen: aanbevolen instanceof HTMLInputElement ? aanbevolen : null,
    };
    return filtersDrawerCache;
  }

  /**
   * Find a drawer checkbox by exact value.
   * @param {string} filterValue
   * @returns {HTMLInputElement|null}
   */
  function findDrawerCheckboxByExactValue(filterValue) {
    const refs = getFiltersDrawerRefs();
    const drawer = refs?.drawer;
    if (!drawer) return null;

    /** @param {any} s */
    const norm = (s) => String(s || '').trim().toLowerCase();
    const wanted = norm(filterValue);
    if (!wanted) return null;

    /** @type {HTMLInputElement[]} */
    const inputs = Array.from(drawer.querySelectorAll('input[type="checkbox"]')).filter((el) => el instanceof HTMLInputElement);
    if (!inputs.length) return null;

    let exact = inputs.find((i) => norm(i.dataset?.label) === wanted || norm(i.value) === wanted);
    if (exact) return exact;

    // Fallback/alias for desserts (Dutch: Nagerechten / Toetjes)
    if (wanted === 'nagerechten' || wanted === 'toetjes') {
      exact = inputs.find((i) => {
        const l = norm(i.dataset?.label);
        const v = norm(i.value);
        return l === 'nagerechten' || l === 'toetjes' || v === 'nagerechten' || v === 'toetjes';
      });
      if (exact) return exact;
    }

    try {
      for (const input of inputs) {
        const id = input.id;
        if (!id) continue;
        const label = drawer.querySelector(`label[for="${CSS.escape(id)}"]`);
        if (!(label instanceof HTMLElement)) continue;
        if (norm(label.textContent || '') === wanted) return input;
      }
    } catch {
      // ignore
    }
    return null;
  }

  // --- Filter helpers ---

  /**
   * When entering soup/dessert step, auto-apply the corresponding filter if present.
   * @param {'soup'|'dessert'} step
   * @returns {Promise<boolean>}
   */
  async function applyExclusiveStepFilters(step, options = {}) {
    const refs = getFiltersDrawerRefs();
    if (!refs) return false;
    const { drawer, alle, aanbevolen } = refs;

    const exactValue = getExactFilterValueForStep(step);
    if (!exactValue) return false;

    const stepFilter = findDrawerCheckboxByExactValue(exactValue);
    if (!(stepFilter instanceof HTMLInputElement)) return false;

    /** @type {HTMLInputElement[]} */
    const checked = Array.from(drawer.querySelectorAll('input[type="checkbox"]:checked')).filter(
      (el) => el instanceof HTMLInputElement
    );
    checked.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const name = String(input.name || '');
      const keepInventory = name.includes('inventory_levels');
      const keepAvailability = name.includes('availability');
      if (keepInventory || keepAvailability) return;
      input.checked = false;
    });

    if (aanbevolen) aanbevolen.checked = false;
    if (alle) alle.checked = true;
    stepFilter.checked = true;

    const facetsFormComponent =
      stepFilter.closest('facets-form-component') || drawer.querySelector('facets-form-component');
    if (facetsFormComponent && 'updateFilters' in facetsFormComponent && typeof facetsFormComponent.updateFilters === 'function') {
      await facetsFormComponent.updateFilters(options);
      return true;
    }

    stepFilter.dispatchEvent(new Event('change', { bubbles: true }));
    await new Promise((resolve) => window.setTimeout(resolve, 100));
    return true;
  }

  /**
   * Clear all non-constraint filters and switch to "Alle".
   * @returns {Promise<boolean>}
   */
  async function applyAlleOnly(options = {}) {
    const refs = getFiltersDrawerRefs();
    if (!refs || !refs.alle) return false;
    const { drawer, alle, aanbevolen } = refs;

    if (aanbevolen) aanbevolen.checked = false;

    /** @type {HTMLInputElement[]} */
    const checked = Array.from(drawer.querySelectorAll('input[type="checkbox"]:checked')).filter(
      (el) => el instanceof HTMLInputElement
    );
    checked.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (input === alle) return;
      const name = String(input.name || '');
      const keepInventory = name.includes('inventory_levels');
      const keepAvailability = name.includes('availability');
      if (keepInventory || keepAvailability) return;
      input.checked = false;
    });

    alle.checked = true;

    const facetsFormComponent = alle.closest('facets-form-component') || drawer.querySelector('facets-form-component');
    if (facetsFormComponent && 'updateFilters' in facetsFormComponent && typeof facetsFormComponent.updateFilters === 'function') {
      await facetsFormComponent.updateFilters(options);
      return true;
    }

    alle.dispatchEvent(new Event('change', { bubbles: true }));
    await new Promise((resolve) => window.setTimeout(resolve, 100));
    return true;
  }

  /**
   * When navigating to a person step (p1, p2, etc.), switch filters to "Aanbevolen".
   * Skips updateFilters() if Aanbevolen is already checked (e.g. p1->p2 with same filters).
   * @returns {Promise<boolean>}
   */
  async function applyAanbevolenForPersonStep(options = {}) {
    const refs = getFiltersDrawerRefs();
    if (!refs || !refs.aanbevolen) return false;
    const { drawer, alle, aanbevolen } = refs;

    if (aanbevolen.checked) return true;

    if (alle) alle.checked = false;

    /** @type {HTMLInputElement[]} */
    const checkedInputs = Array.from(drawer.querySelectorAll('input[type="checkbox"]:checked')).filter(
      (el) => el instanceof HTMLInputElement
    );

    checkedInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (input === aanbevolen) return;
      if (alle && input === alle) return;
      const name = String(input.name || '');
      const keepInventory = name.includes('inventory_levels');
      const keepAvailability = name.includes('availability');
      if (keepInventory || keepAvailability) return;
      input.checked = false;
    });

    aanbevolen.checked = true;

    const facetsFormComponent =
      aanbevolen.closest('facets-form-component') || drawer.querySelector('facets-form-component');
    if (facetsFormComponent && 'updateFilters' in facetsFormComponent && typeof facetsFormComponent.updateFilters === 'function') {
      await facetsFormComponent.updateFilters(options);
      return true;
    }

    aanbevolen.dispatchEvent(new Event('change', { bubbles: true }));
    await new Promise((resolve) => window.setTimeout(resolve, 100));
    return true;
  }

  /** @param {{ skipProductCardUpdate?: boolean; skipViewTransition?: boolean }} [options] @returns {Promise<boolean>} */
  async function applyAlleOnlyWithRafRetry(options = {}) {
    const first = await applyAlleOnly(options);
    if (first) return true;
    await new Promise((resolve) => {
      try {
        requestAnimationFrame(() => resolve(undefined));
      } catch {
        window.setTimeout(() => resolve(undefined), 0);
      }
    });
    return await applyAlleOnly(options);
  }

  /** @param {'soup'|'dessert'} step @param {{ skipProductCardUpdate?: boolean; skipViewTransition?: boolean }} [options] @returns {Promise<boolean>} */
  async function applyExclusiveStepFiltersWithRafRetry(step, options = {}) {
    const first = await applyExclusiveStepFilters(step, options);
    if (first) return true;
    await new Promise((resolve) => {
      try {
        requestAnimationFrame(() => resolve(undefined));
      } catch {
        window.setTimeout(() => resolve(undefined), 0);
      }
    });
    return await applyExclusiveStepFilters(step, options);
  }

  /** @param {{ skipProductCardUpdate?: boolean; skipViewTransition?: boolean }} [options] @returns {Promise<boolean>} */
  async function applyAanbevolenForPersonStepWithRafRetry(options = {}) {
    const first = await applyAanbevolenForPersonStep(options);
    if (first) return true;
    await new Promise((resolve) => {
      try {
        requestAnimationFrame(() => resolve(undefined));
      } catch {
        window.setTimeout(() => resolve(undefined), 0);
      }
    });
    return await applyAanbevolenForPersonStep(options);
  }

  /**
   * Options for step-change flows where syncCartAndProductCardsForStepChange runs after.
   * Skips redundant product card update and view transition for speed.
   */
  const STEP_CHANGE_OPTIONS = { skipProductCardUpdate: true, skipViewTransition: true };

  /**
   * Centralized filter sync when moving between steps.
   * @param {string | null | undefined} fromHandle
   * @param {string | null | undefined} toHandle
   * @param {{ setLastFilterStepApplied: (v: string|null) => void }} state
   * @param {{ skipProductCardUpdate?: boolean; skipViewTransition?: boolean }} [options] - Pass STEP_CHANGE_OPTIONS when syncCartAndProductCardsForStepChange runs next
   * @returns {Promise<void>}
   */
  async function syncFiltersForStepChange(fromHandle, toHandle, state, options = {}) {
    const to = String(toHandle || '').trim();
    if (!to) return;

    const isPerson = /^p\d+$/i.test(to);
    const isToAddOn = to === 'soup' || to === 'dessert';
    const isToOneoff = to === 'oneoff';

    const opts = options;

    if (isToOneoff) {
      const applied = await applyAlleOnlyWithRafRetry(opts);
      if (applied) state.setLastFilterStepApplied('oneoff');
      return;
    }

    if (isToAddOn) {
      const applied = await applyExclusiveStepFiltersWithRafRetry(/** @type {'soup'|'dessert'} */ (to), opts);
      if (applied) state.setLastFilterStepApplied(to);
      return;
    }

    if (isPerson) {
      await applyAanbevolenForPersonStepWithRafRetry(opts);
      state.setLastFilterStepApplied(null);
    }
  }

  function scrollToTopOnStepChange() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // --- State helpers (sessionStorage) ---

  /** @returns {string} */
  function getActiveStepHandle() {
    try {
      const raw = sessionStorage.getItem(ACTIVE_STEP_KEY);
      return String(raw || '').trim() || 'p1';
    } catch {
      return 'p1';
    }
  }

  /** @param {string} handle */
  function setActiveStepHandle(handle) {
    const h = String(handle || '').trim() || 'p1';
    try {
      sessionStorage.setItem(ACTIVE_STEP_KEY, h);
    } catch {
      // ignore
    }
  }

  /** @returns {boolean} */
  function isOneoffEnabled() {
    try {
      return String(sessionStorage.getItem(ONEOFF_ENABLED_KEY) || '').trim() === '1';
    } catch {
      return false;
    }
  }

  /** @param {boolean} enabled */
  function setOneoffEnabled(enabled) {
    try {
      sessionStorage.setItem(ONEOFF_ENABLED_KEY, enabled ? '1' : '0');
    } catch {
      // ignore
    }
  }

  /**
   * Build step handles from survey payload.
   * @param {any | null} payload
   * @returns {string[]}
   */
  function buildStepHandlesFromPayload(payload) {
    const { profileCount, soupCount, dessertCount } = getStepperCounts(payload);
    const oneoffEnabled = isOneoffEnabled();

    /** @type {string[]} */
    const handles = [];
    for (let i = 1; i <= Math.min(6, Math.max(1, profileCount)); i++) {
      handles.push(`p${i}`);
    }
    if (soupCount > 0) handles.push('soup');
    if (dessertCount > 0) handles.push('dessert');
    if (oneoffEnabled) handles.push('oneoff');
    return handles;
  }

  const utilsExport = {
    ACTIVE_STEP_KEY,
    ONEOFF_ENABLED_KEY,
    STEP_PROGRESS_LABELS,
    getItemKind,
    getItemProfileKey,
    getStepperCounts,
    getStepCounts,
    isStepQuotaFull,
    getSurveyResultPayload,
    getRecommendationsPayload,
    getExactFilterValueForStep,
    findDrawerCheckboxByExactValue,
    applyExclusiveStepFilters,
    applyAlleOnly,
    applyAanbevolenForPersonStep,
    applyAlleOnlyWithRafRetry,
    applyExclusiveStepFiltersWithRafRetry,
    applyAanbevolenForPersonStepWithRafRetry,
    syncFiltersForStepChange,
    STEP_CHANGE_OPTIONS,
    scrollToTopOnStepChange,
    getActiveStepHandle,
    setActiveStepHandle,
    isOneoffEnabled,
    setOneoffEnabled,
    buildStepHandlesFromPayload,
  };

  window.CollectionSubscriptionFooterUtils = utilsExport;
})();
